/** Trigger a browser download for an in-memory blob. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const SVG_NS = 'http://www.w3.org/2000/svg';

/** Rasterize the first <svg> inside `container` to a 2x PNG and download it.
 *  Works without dependencies because the call-flow diagram is self-contained
 *  (inline fills/strokes/markers, no external CSS or images — so the canvas
 *  is never tainted). A title line is drawn above the diagram. */
export async function exportSvgAsPng(container: HTMLElement, filename: string, title: string): Promise<void> {
  const svg = container.querySelector('svg');
  if (!svg) throw new Error('No diagram to export');

  const vb = svg.viewBox.baseVal;
  const HEADER = 36;
  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.removeAttribute('style');
  clone.setAttribute('width', String(vb.width));
  clone.setAttribute('height', String(vb.height + HEADER));
  clone.setAttribute('viewBox', `0 ${-HEADER} ${vb.width} ${vb.height + HEADER}`);
  // Standalone SVGs have no page styles — pin the font or it renders in Times
  clone.setAttribute('font-family', "'Segoe UI', system-ui, -apple-system, sans-serif");

  const titleEl = document.createElementNS(SVG_NS, 'text');
  titleEl.setAttribute('x', '8');
  titleEl.setAttribute('y', String(-HEADER + 22));
  titleEl.setAttribute('font-size', '14');
  titleEl.setAttribute('font-weight', '600');
  titleEl.setAttribute('fill', '#111827');
  titleEl.textContent = title;
  clone.insertBefore(titleEl, clone.firstChild);

  const xml = new XMLSerializer().serializeToString(clone);
  const svgUrl = URL.createObjectURL(new Blob([xml], { type: 'image/svg+xml;charset=utf-8' }));
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Could not render the diagram'));
      img.src = svgUrl;
    });

    const SCALE = 2;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(vb.width * SCALE);
    canvas.height = Math.round((vb.height + HEADER) * SCALE);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas not available');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const png = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(b => (b ? resolve(b) : reject(new Error('PNG encoding failed'))), 'image/png'));
    saveBlob(png, filename);
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
}
